import {DATE_FORMAT, DATE_FORMAT_HYPHEN, DATE_TIME_FORMAT_HYPHEN} from "./ConfigConstants";
import * as React from 'react';
import {Moment} from "moment-timezone";
import {Box, DateRangePicker} from "@amzn/awsui-components-react-v3";
import {UTC_TIME_END_DAY, UTC_TIME_START_DAY} from "../pages/Configuration/SelfServeConstants";
import {EquipmentDataDropDownInput} from "../pages";
import { addYears } from 'date-fns';
let moment = require('moment-timezone');

const timeUnit = {
  SECOND: "second",
  MINUTE: "minute",
  HOUR: "hour",
  DAY: "day",
  WEEK: "week",
  MONTH: "month",
  YEAR: "year"
}

export const UTC = 'UTC';
export const localTimezoneName = moment.tz.guess();
const timeZoneOffsetName = moment(new Date()).format("[GMT] Z");
const timeZoneHintText = `* All Dates in timezone: ${localTimezoneName} (${timeZoneOffsetName})`;

export const timeZoneAbbreviation = moment.tz(localTimezoneName).zoneAbbr();
export const timeZoneHint = (<Box variant="span" color="text-status-inactive">
      <i>{timeZoneHintText}</i> 
    </Box>);

type DateRangePickerProps = {
  value: any,
  onChange: (detail: any) => any,
  disabled: boolean
  isDateEnabled: (date: any) => any
}

/**
 * This method would be used for parsing the epoch Milliseconds, that are stored in backend,
 * and would be displayed in the following format:
 * i.e. 12/10/2020
 * @param epochMilli
 * @param dateFormat
 */
export function getHumanReadableDate(epochMilli: string) {
  if(epochMilli == null) {
    return;
  }
  return moment(parseInt(epochMilli)).format(DATE_FORMAT);
}

export function getHumanReadableDateInDateFormat(epochMilli: string , dateformat: string) {
  if(epochMilli == null) {
    return;
  }
  return moment(parseInt(epochMilli)).format(dateformat);
}

export function getHumanReadableUTCDate(epochMilli: string, dateformat: string) {
  if (epochMilli == null) {
    return;
  }
  return moment.utc(parseInt(epochMilli)).format(dateformat);
}

export function formatDate(date: Date, format: string): string {
  return moment(date.getTime()).format(format);
}

export function formatDateString(date: string, format: string): string {
  return moment(date).format(format);
}

export function formatDateTimeString(date: string, time: string): string | undefined {
  if (date && time) {
    return date + " " + time;
  }
  return;
}

export function getHumanReadableDateTime(timeInEpochMilli: string | undefined, timezone: string, format: string) {
  return timeInEpochMilli && timezone && format ? moment(parseInt(timeInEpochMilli)).tz(timezone).format(format) : null;
}

export function getHumanReadableDateTimeForEpochSecond(timeInEpochSeconds: string| undefined, timezone: string, format: string) {
  const timeInEpochMilli : string | undefined = timeInEpochSeconds ? (parseInt(timeInEpochSeconds) * 1000).toString() : undefined;
  return getHumanReadableDateTime(timeInEpochMilli, timezone, format);
}

export function getHumanReadableDateTimeWithOffset(timeInEpochMilli: string, format: string) {
  var localDateTime = getHumanReadableDateInDateFormat(timeInEpochMilli, format);
  var utcOffset = getUTCOffsetFromIANA(localTimezoneName, "Z");
  return localDateTime && utcOffset ? localDateTime + utcOffset : null;
}

/**
 * This method is used to render Time Range on UI.
 * @param startTime
 * @param endTime
 * @param timezone
 */
export function getDisplayTimeRange(startTime: string | undefined, endTime: string | undefined, timezone: string): undefined|string {
  if (startTime == null && endTime == null) {
    return;
  } else if (startTime == null) {
    return getHumanReadableDateTime(endTime, timezone, DATE_TIME_FORMAT_HYPHEN);
  } else if (endTime == null) {
    return getHumanReadableDateTime(startTime, timezone, DATE_TIME_FORMAT_HYPHEN);
  } else {
    return getHumanReadableDateTime(startTime, timezone, DATE_TIME_FORMAT_HYPHEN) + " - " + getHumanReadableDateTime(endTime, timezone, DATE_TIME_FORMAT_HYPHEN);
  }
}

export function getEpochMilli(dateStr: string, endOfDay?: boolean) {
  if (!dateStr) {
    return null;
  }
  const date = new Date(dateStr);
  if (endOfDay) {
    date.setHours(23,59,59,999);
  }
  return date.getTime().toString();
}

export function getEpochMilliInNumber(dateStr: string) : number | null{
  if (!dateStr) {
    return null;
  }
  const date = new Date(dateStr);
  return date.getTime();
}

export function getEpochMillisNow() {
  const date = new Date();
  return (date.getTime()).toString();
}

export function isDateRangeInvalid(start: string | null, end: string | null) {
  if (start && end && start > end) return true;
  return false;
}

export function isTimeAfter(currentTime: string | null, startTime: string | null) {
  if (currentTime && startTime) {
    const currentDate = getHumanReadableDateInDateFormat(currentTime, DATE_FORMAT_HYPHEN);
    const startDate = getHumanReadableDateInDateFormat(startTime, DATE_FORMAT_HYPHEN);

    if (new Date(currentDate) > new Date(startDate)) return true;
  }
  return false;
}

export function compareDateWithCurrentTime(date: string) {
  if (date === '-') {
    return false;
  }
  const currentTime = getCurrentDateInUTCEpochSeconds();
  const dateTime = getCurrentDateInUTCEpochSecondsFromDateAndTimezone(new Date(date), localTimezoneName);
  return dateTime <= currentTime;
}

export function sortInstantComparator(epochMilli1: string, epochMilli2: string) {
   return (epochMilli1 === '0') ? 1 : (epochMilli2 === '0') ? -1 : moment(parseInt(epochMilli1)).isBefore(moment(parseInt(epochMilli2))) ? -1 : 1;
}

export function sortEpochComparator(epochMilli1: number | undefined, epochMilli2: number | undefined): number {
  if (epochMilli1 === epochMilli2) return 0;
  if (epochMilli2 === undefined) return -1;
  if (epochMilli1 === undefined) return 1;
  return epochMilli1 - epochMilli2;
}

export function sortStringComparator(value1: string, value2: string) {
  const isEmpty = (val: string) => val === undefined || val === null || val === '';

  // If both values are empty, consider them equal
  if (isEmpty(value1) && isEmpty(value2)) return 0;

  // If only value1 is empty, it should come after value2
  if (isEmpty(value1)) return 1;

  // If only value2 is empty, it should come after value1
  if (isEmpty(value2)) return -1;

  return value1 < value2 ? -1 : value1 > value2 ? 1 : 0;
}

export function sortTimestampComparator(value1: string, value2: string) {

  if (value1 === "-" && value2 === "-") return 0;
   // Move empty dates to bottom
  if (value1 === "-") return 1; 
  if (value2 === "-") return -1;

  return new Date(value1).getTime() - new Date(value2).getTime();
}

export function sortNumberComparator(value1: string, value2: string) {
  let num1 = Number(value1)
  let num2 = Number(value2)
  return num1 < num2 ? -1 : num1 > num2 ? 1 : 0;
}

export function getMoment(time: string, timezone: string) {
  return time && timezone ? moment.tz(time, timezone) : null;
}

export function getEpochMilliFromMoment(time: Moment) {
  return time ? time.valueOf().toString() : null;
}

export function getEpochFromEpochMili(epochmili: number) {
  return Math.floor(epochmili / 1000);
}

export function getISOWeek(weekNumber: number) {
  const momentObj = moment().add(weekNumber, 'weeks').tz(UTC);
  const isoWeek = momentObj.isoWeek();
  const year = momentObj.isoWeekYear();
  return `${year}W${isoWeek.toString().padStart(2, '0')}`;
}

export function getIsoWeekFromEpoch(epochTimestamp: number) {
  const momentObj = moment(epochTimestamp).tz(UTC);
  const isoWeek = momentObj.isoWeek();
  const year = momentObj.isoWeekYear();

  return `${year}W${isoWeek.toString().padStart(2, '0')}`;
}

export function getIsoDayFromEpoch(epochTimestamp: number) {
  const momentObj = moment(epochTimestamp).tz(UTC);

  return momentObj.format('dddd');
}

export function getCurrentDateInUTCEpochSeconds(): number  {
  return getCurrentDateInUTCEpochSecondsFromDateAndTimezone(new Date(), localTimezoneName);
}

export function getCurrentDateInUTCEpochSecondsFromDateAndTimezone(currentDate: Date, timezone: string): number {
  return getCurrentDateInUTCEpochSecondsFromDateStringAndTimezone(moment.tz(currentDate, timezone).startOf('day').format('YYYY-MM-DD HH:mm'), timezone).getTime()/1000;
}

function getCurrentDateInUTCEpochSecondsFromDateStringAndTimezone(localDate: string, timezone: string): Date {
  const date = moment.tz(localDate, UTC);
  return date.toDate();
}

export function getCurrentDateStartOfDayInUTCEpoch() : number {
  const currentDate = new Date();
  currentDate.setUTCHours(0, 0, 0, 0);
  return currentDate.getTime();
}

export function getCurrentDatePlusTwoYears(): number {
  const currentDate = getCurrentDateInUTCEpochSeconds();

  return addYears(currentDate * 1000, 2).getTime()/1000;
}

export function getCurrentDateMinusTwoYears(): number {
  const currentDate = getCurrentDateInUTCEpochSeconds();

  return addYears(currentDate * 1000, -2).getTime()/1000;
}

export function getThirdSaturdayFromToday(): number {

  const givenDate = new Date();
  const dayOfWeek = givenDate.getDay();
  const daysUntilSaturday = 6 - dayOfWeek;
  const nextSaturday = new Date(givenDate.getTime());

  nextSaturday.setUTCDate(givenDate.getUTCDate() + daysUntilSaturday);
  nextSaturday.setUTCHours(0, 0, 0, 0);

  const thirdSaturday = new Date(nextSaturday);
  thirdSaturday.setDate(nextSaturday.getDate() + 7 * 2);

  return (thirdSaturday.getTime() / 1000);
}

export function getAbbreviatedTimezoneFromIANA(timezoneIANA: string) {
  return timezoneIANA ? moment().tz(timezoneIANA).zoneAbbr() : null
}

export function getUTCOffsetFromIANA(timezoneIANA: string, format?: string) {
  return timezoneIANA ? moment().tz(timezoneIANA).format(format? format : "[UTC] Z") : null
}

export function getDateAfterTime(numberOfDays: number | null, hours: number | null, minutes: number | null) {
  var date = new Date();
  if (numberOfDays) {
    date.setDate(date.getDate() + numberOfDays);
  }
  if (hours) {
    date.setHours(date.getHours() + hours);
  }
  if (minutes) {
    date.setMinutes(date.getMinutes() + minutes);
  }
  return date;
}

export function getStartDateFromRelativeRange(range: any) {
  const startDate = new Date();
  switch (range.unit) {
    case timeUnit.SECOND:
      startDate.setSeconds(startDate.getSeconds() - range.amount);
      break;
    case timeUnit.MINUTE:
      startDate.setMinutes(startDate.getMinutes() - range.amount);
      break;
    case timeUnit.HOUR:
      startDate.setHours(startDate.getHours() - range.amount);
      break;
    case timeUnit.DAY:
      startDate.setDate(startDate.getDate() - range.amount);
      break;
    case timeUnit.WEEK:
      startDate.setDate(startDate.getDate() - range.amount * 7);
      break;
    case timeUnit.MONTH:
      startDate.setMonth(startDate.getMonth() - range.amount);
      break;
    case timeUnit.YEAR:
      startDate.setFullYear(startDate.getFullYear() - range.amount);
      break;
  }
  return startDate;
}

export function getUTCStartOfDay(date: string) {
  return Date.parse(date + UTC_TIME_START_DAY);
}

export function getUTCEndOfDay(date: string) {
  return Date.parse(date + UTC_TIME_END_DAY);
}

export function getNumberOfDays(startDate: number, endDate: number) {
  return (endDate - startDate)/1000/60/60/24;
}

export function getTimezoneByLocationOrDefaultTimezone(location: string, equipmentDataDropDownInput: EquipmentDataDropDownInput[] | undefined) {
  var timezone = '';
  equipmentDataDropDownInput?.forEach((eq) => {
    if (eq.id === location) {
      //Many-a-times, the timezone in the eqs file is null
      //In such a case, fallback to the local timezone, preventing the page from breaking
      timezone = eq.timezone || localTimezoneName;
    }
  });
  if (timezone === '') {
    return localTimezoneName;
  }
  return timezone;
}

export function getDateRangePicker(props: DateRangePickerProps) {
  return (<DateRangePicker
      value={props.value}
        onChange={({ detail }) => props.onChange(detail)}
        isDateEnabled = {( date ) => props.isDateEnabled(date)}
        relativeOptions={[
          {
            key: "previous-10-minutes",
            amount: 10,
            unit: "minute",
            type: "relative"
          },
          {
            key: "previous-30-minutes",
            amount: 30,
            unit: "minute",
            type: "relative"
          },
          {
            key: "previous-1-hour",
            amount: 1,
            unit: "hour",
            type: "relative"
          },
          {
            key: "previous-6-hours",
            amount: 6,
            unit: "hour",
            type: "relative"
          }
        ]}
        isValidRange={range => {
          if (range?.type === "absolute") {
            const [startDateWithoutTime] = range.startDate.split("T");
            const [endDateWithoutTime] = range.endDate.split("T");
            if (!startDateWithoutTime || !endDateWithoutTime) {
              return {
                valid: false,
                errorMessage: "The selected date range is incomplete. Select a start and end date for the date range."
              };
            }
            if (range.startDate > range.endDate) {
              return {
                valid: false,
                errorMessage: "Start date should be less than end date."
              };
            }
          }
          return { valid: true };
        }}
        i18nStrings={{
          todayAriaLabel: "Today",
          nextMonthAriaLabel: "Next month",
          previousMonthAriaLabel: "Previous month",
          customRelativeRangeDurationLabel: "Duration",
          customRelativeRangeDurationPlaceholder: "Enter duration",
          customRelativeRangeOptionLabel: "Custom range",
          customRelativeRangeOptionDescription: "Set a custom range in the past",
          customRelativeRangeUnitLabel: "Unit of time",
          formatRelativeRange: time => {
            const value = (1 === time.amount) ? time.unit : `${time.unit}s`;
            return `Last ${time.amount} ${value}`;
          },
          formatUnit: (unit, amount) => ((1 === amount) ? unit : `${unit}s`),
          dateTimeConstraintText: "For time, use 24 hr format.",
          relativeModeTitle: "Relative range",
          absoluteModeTitle: "Absolute range",
          relativeRangeSelectionHeading: "Choose a range",
          startDateLabel: "Start date",
          endDateLabel: "End date",
          startTimeLabel: "Start time",
          endTimeLabel: "End time",
          clearButtonLabel: "Clear and dismiss",
          cancelButtonLabel: "Cancel",
          applyButtonLabel: "Apply"
        }}
        disabled={props.disabled}
        placeholder="Filter by a date and time range"
      />);
}